// Libraries
import { Flex, Text } from '@chakra-ui/react';
import { Fragment } from 'react';
// Components
import { DBIcon } from '@atoms/Icons';
// Utils
import translation from '@translations/en.json';
// Types
import { DataStateWrapperProps } from './DataStateWrapper.type';

const text = translation.data_state_wrapper;

/**
 * A wrapper component that provides a mechanism to display different states of data rendering, such as an empty state.
 *
 * @component
 * @param {DataStateWrapperProps} props - The props for the DataStateWrapper component.
 * @param {ReactNode} props.children - The content to be wrapped by the component.
 * @param {boolean} props.isEmpty - Flag indicating whether the data is considered empty.
 * @param {string} [props.titleEmpty] - The title to be displayed in the empty state. Defaults to a predefined translation.
 * @param {string} [props.descriptionEmpty] - The description to be displayed in the empty state. Defaults to a predefined translation.
 * @param {FlexProps} [props.styles] - Additional styling props to be passed to the underlying Flex component.
 * @returns {ReactElement} The DataStateWrapper component.
 */
export const DataStateWrapper = (props: DataStateWrapperProps) => {
  const {
    children,
    isEmpty,
    titleEmpty = text.not_enough_data.title,
    descriptionEmpty = text.not_enough_data.description,
    ...styles
  } = props;

  if (isEmpty) {
    return (
      <Flex h='full' p={8} {...styles}>
        <Flex direction='column' align='center' justify='space-between' m='auto' w={84}>
          <DBIcon w={7.5} h={7.5} />
          <Text color='grey.100' fontSize='lg' textAlign='center' mt={4}>
            {titleEmpty}
          </Text>
          <Text color='grey.100' fontSize='lg' textAlign='center' mt={4}>
            {descriptionEmpty}
          </Text>
        </Flex>
      </Flex>
    );
  }

  return <Fragment>{children}</Fragment>;
};
